const BAF_TOKEN_ALERT_DISMISSED = "BAFTokenAlertDismissed";

import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { forwardRef, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnsureFocus } from "../../../hooks/useEnsureFocus";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../../utils/useBreakpoints";
import { SquiggleIconBox } from "../../icons/SquiggleIconBox";
import { Avatar } from "../../user/Avatar";
import { useUserData } from "../../../hooks/useUserData";
import { ArtistMenu } from "./ArtistMenu";
import { MenuItem } from "./MenuItem";
import { useIsSidebarOpen } from "../../../hooks/useIsSidebarOpen";
import { useSlideInMenu } from "../../slideInMenu/hooks/useSlideInMenu";
import { GoldUpsellBanner } from "./GoldUpsellBanner";
import { isMobile } from "react-device-detect";
import { setFunnelModalConfig } from "../../funnel/FunnelModal";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { useAccountData } from "../../../hooks/useAccountData";
import { useNavigate } from "react-router";
import { ContextReferrer, FunnelContextArea } from "../../funnel/types";
import { ComplexMenuItem } from "./ComplexMenuItem";
import { logout } from "../../auth/api/authApi";
import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";
import { AttentionIcon } from "../../icons/AttentionIcon";
import { CopyIcon } from "../../icons/CopyIcon";
import { useTopBarHeight } from "../../../hooks/userTopBarHeight";
import { useRibbonBannerHeight } from "../../../hooks/useRibbonBannerHeight";

type AccountMenuItem = {
  url: string;
  label: React.ReactNode;
  noArrow?: boolean;
  onClick?: () => void;
};

export function AccountMenu() {
  const [t] = useTranslation();

  const accountData = useAccountData();
  const { userData } = useUserData();
  const isMobileScreen = useMobileBreakpoint();
  const popoverRef = useRef(null);
  const ensureFocus = useEnsureFocus();
  const toast = useToast();
  const { setIsSidebarOpen } = useIsSidebarOpen();
  const isSmall = useIsSmallBreakpoint();
  const navigate = useNavigate();
  const isGoldUser = userData?.gold;
  const [showAlert, setShowAlert] = useState(true);
  const { openUserProfile } = useSlideInMenu();
  const topBarHeight = useTopBarHeight();

  const isNewOnboarding = accountData?.features?.["new_onboarding"];


  // When the popover appears, the account box should be focused.
  // However, the container is still animating and hidden,
  // so it can't be focused immediately. This code intercepts the
  // focus call and ensures the input gains focus once it is visible.
  const focusHandler = useRef({
    focus: () => {
      if (!popoverRef.current) {
        return;
      }
      ensureFocus(popoverRef.current);
    },
  });

  var bafToken = accountData?.baf_token;
  var isNewToken = false;
  if (bafToken) {
    isNewToken =
      localStorage.getItem(BAF_TOKEN_ALERT_DISMISSED) !==
      bafToken.id.toString();
  }

  const menuItems = useMemo<AccountMenuItem[]>(() => {
    if (!userData) return [];
    return [
      {
        url: `${userData.edit_url}profile`,
        label: t("components.navigation.me.accountSettings"),
      },
      {
        url: `${userData.url}/lists`,
        label: t("components.navigation.me.lists"),
      },
      userData.onboarding === "completed" && isNewOnboarding
        ? {
            onClick: () =>
              setFunnelModalConfig({
                type: FUNNEL_TYPES.onboarding,
                context: {
                  modalContext: FunnelContextArea.settings,
                },
                options: {
                  trapFocus: true,
                  newOnboarding: true,
                },
              }),
            label: t("components.navigation.me.archetypes"),
            buttonText: t("components.navigation.me.change"),
          }
        : null,
      userData.onboarding === "completed"
        ? {
            onClick: () =>
              setFunnelModalConfig({
                type: FUNNEL_TYPES.onboarding,
                context: {
                  modalContext: FunnelContextArea.settings,
                },
                options: {
                  trapFocus: true,
                },
              }),
            label: t("components.navigation.me.interests"),
            buttonText: t("components.navigation.me.change"),
          }
        : null,
      {
        url: `${userData.edit_url}credits`,
        label: t("components.navigation.me.promoCodes"),
      },
      {
        url: "/my-shelf/purchases",
        label: t("components.navigation.me.purchases"),
        noArrow: true,
      },
      {
        url: `${userData.edit_url}subscriptions`,
        label: t("components.navigation.me.subscriptions"),
      },
      accountData?.can_use_admin_panel
        ? {
            url: "/admin/",
            label: t("components.navigation.me.siteAdministration"),
          }
        : undefined,
      {
        label: t("components.navigation.me.submitFeedback"),
        url: "/support/webnext-feedback",
      },
      {
        url: "/forums/21/bug-reporting",
        label: t("components.navigation.me.reportABug"),
      },
      userData.is_admin
        ? {
            url: "/admin-next",
            label: "Admin Next",
          }
        : undefined,
    ].filter((n) => n !== undefined) as AccountMenuItem[];
  }, [accountData, userData]);

  const copyBaf = async () => {
    if (!accountData?.baf_token?.token) return;
    await navigator.clipboard.writeText(accountData.baf_token.token);
    toast({
      title: t("components.navigation.me.bafToast"),
      status: "success",
    });
  };

  const goldUpsellTestEnabled = accountData?.features["gold_upsell"];

  const ribbonBannerHeight = useRibbonBannerHeight();

  if (!userData) return null;

  return (
    <Popover
      gutter={1}
      variant="navbar"
      initialFocusRef={focusHandler}
      isLazy
      onClose={_handleBafTokenAlert}
    >
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <AccountMenuTrigger
              onClick={() => {
                if (isSmall) {
                  setIsSidebarOpen(false);
                }
              }}
              showAlert={showAlert && isNewToken}
            />
          </PopoverTrigger>
          <PopoverContent
            className="navbar-popover"
            ref={popoverRef}
            rootProps={{
              zIndex: -1,
            }}
            variants={{
              enter: { opacity: 1, top: ribbonBannerHeight, right: 0 },
              exit: { opacity: 0, top: -topBarHeight.get(), right: 0 },
            }}
            height={
              isMobileScreen
                ? `calc(100% - ${ribbonBannerHeight}px)`
                : undefined
            }
            style={{ overscrollBehavior: "contain" }}
            maxHeight={isMobileScreen ? "100%" : "90%"}
            width={isMobileScreen ? "100%" : "365px"}
            borderRadius={isMobileScreen ? 0 : undefined}
          >
            <PopoverHeader pb="0px">
              <Flex direction="row" justifyContent="space-between">
                <Flex direction="column">
                  <Text
                    marginBottom="1px"
                    fontSize={
                      (userData?.name?.length ?? 0) > 22 ? "14px" : "20px"
                    }
                    fontWeight="700"
                    lineHeight="26px"
                    color="dune.dune"
                    fontFamily="Bricolage Grotesque"
                    style={{ scrollbarGutter: "auto" }}
                  >
                    {userData?.name}
                  </Text>
                  <SquiggleIconBox
                    color="blaze.blaze"
                    width="68px"
                    height="auto"
                  />
                </Flex>
                <Button
                  variant="link"
                  color="blaze.blaze"
                  fontSize={
                    (userData?.name?.length ?? 0) > 22 ? "12px" : "14px"
                  }
                  fontWeight="500"
                  letterSpacing=".5px"
                  paddingTop="4px"
                  height="auto"
                  minHeight="auto"
                  onClick={() => {
                    if (userData?.name) {
                      openUserProfile(userData.name, {
                        area: FunnelContextArea.settings,
                      });
                      onClose();
                      _handleBafTokenAlert();
                    }
                  }}
                >
                  {t("components.navigation.me.viewProfile")}
                </Button>
              </Flex>
            </PopoverHeader>
            <PopoverBody className="styled-scrollbar">
              <Flex
                direction="column"
                paddingBottom={isMobile ? "60px" : undefined}
              >
                {accountData?.baf_token && (
                  <>
                    <ComplexMenuItem
                      title={t("components.navigation.me.baf")}
                      subtitle={t("components.navigation.me.bafSubtitle", {
                        remaining: accountData.baf_token.remaining_uses,
                        total: accountData.baf_token.original_uses,
                        expires: accountData.baf_token.expires_in,
                      })}
                      icon={<CopyIcon />}
                      prefixIcon={
                        showAlert && isNewToken ? (
                          <AttentionIcon
                            boxSize="20px"
                            color="warning.400"
                            transition="inherit"
                          />
                        ) : undefined
                      }
                      onClick={copyBaf}
                    />
                    <Box
                      height="2px"
                      background="transparent.white.10"
                      width="full"
                      mt="16px"
                      mb="12px"
                    />
                  </>
                )}
                {!isGoldUser && (
                  <>
                    <GoldUpsellBanner
                      onClick={
                        goldUpsellTestEnabled
                          ? () => {
                              navigate("/gold");
                              rootComponentsStore.eventContextReferrer.set(
                                ContextReferrer.settings,
                              );
                              onClose();
                              _handleBafTokenAlert();
                            }
                          : undefined
                      }
                    />
                    <Box
                      height="2px"
                      background="transparent.white.10"
                      width="full"
                      mt="16px"
                      mb="12px"
                    />
                  </>
                )}
                {accountData?.features["change_username"] && (
                  <MenuItem
                    key="username"
                    label="Change Username"
                    underline
                    onClick={() => {
                      setFunnelModalConfig({
                        type: FUNNEL_TYPES.changeUsername,
                        context: {
                          modalContext: FunnelContextArea.settings,
                        },
                      });
                    }}
                  />
                )}
                {menuItems.map((item, index) =>
                  item ? (
                    <MenuItem
                      key={index}
                      onClick={item.onClick}
                      {...item}
                      underline={
                        index !== menuItems.length - 1 ||
                        !userData?.user_creator_data
                      }
                    />
                  ) : null,
                )}
                <ArtistMenu />

                <MenuItem
                  key="logout"
                  label={t("components.navigation.me.logout")}
                  onClick={async () => {
                    onClose();
                    try { await logout(); } catch {}
                    window.location.reload();
                  }}
                  noArrow
                />
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );

  function _handleBafTokenAlert() {
    var bafToken = accountData?.baf_token;
    if (bafToken) {
      localStorage.setItem(BAF_TOKEN_ALERT_DISMISSED, bafToken.id.toString());
      setShowAlert(false);
    }
  }
}

interface AccountMenuTriggerProps extends ButtonProps {
  showAlert?: boolean;
}

const AccountMenuTrigger = forwardRef<HTMLDivElement, AccountMenuTriggerProps>(
  ({ showAlert, ...props }, ref) => {
    const { userData } = useUserData();

    return (
      <Flex ref={ref} height="full" alignItems="flex-end">
        <Tooltip
          variant="navbar"
          label="Account"
          aria-label="account-tooltip"
          isDisabled={!!props["aria-expanded"]}
        >
          <Button
            gc-testing-id="btnProfileMenu"
            variant="navbar"
            _hover={{ bg: "transparent" }}
            _expanded={{ bg: "transparent" }}
            {...props}
          >
            <Avatar
              isGold={userData?.gold}
              username={userData?.name}
              imageSrc={userData?.avatar_tiny_url}
              showAlert={showAlert}
            />
          </Button>
        </Tooltip>
      </Flex>
    );
  },
);
